import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import _moment from 'moment';
import { default as _rollupMoment } from 'moment';
const moment = _rollupMoment || _moment;

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Observable } from 'rxjs';
import { fetchUserAttributes } from 'aws-amplify/auth';

import { SharedService } from '../../layouts/shared-service';
import { AuthService } from '../../service';
import { UserAdminService } from '../../service/user-admin.service';
import { User } from '../../model/types';
import { CognitoUser, UserEvent } from '../../model/user-admin.model';
import { UserAdminDialogComponent, UserDialogData } from './user-admin-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from './confirm-dialog.component';

export const MY_FORMATS = {
    parse: { dateInput: 'MM/YYYY' },
    display: {
        dateInput: 'MM/DD/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-user-admin',
    templateUrl: './user-admin.component.html',
    styleUrls: ['./user-admin.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    standalone: false
})
export class UserAdminComponent implements OnInit {
    @ViewChild('userPaginator', { static: false }) userPaginator: MatPaginator;
    @ViewChild('userSort', { static: false }) userSort: MatSort;
    @ViewChild('eventPaginator', { static: false }) eventPaginator: MatPaginator;
    @ViewChild('eventSort', { static: false }) eventSort: MatSort;

    pageTitle = 'User Administration';
    signedInUser: any;
    illegalUser: boolean = false;

    // User list
    userDataSource: MatTableDataSource<CognitoUser>;
    userColumns: string[] = ['name', 'username', 'email', 'organization', 'access', 'status', 'enabled', 'actions'];
    users: CognitoUser[] = [];
    usersLoading: boolean = false;
    usersError: string = '';

    // User events
    eventDataSource: MatTableDataSource<UserEvent>;
    eventColumns: string[] = ['eventDate', 'eventType', 'username', 'adminUser'];
    events: UserEvent[] = [];
    eventsLoading: boolean = false;
    eventsError: string = '';
    eventStartDate: string;
    eventEndDate: string;
    eventUsername: string = '';
    eventAction: string = '';
    eventActionOptions: string[] = ['CREATE', 'UPDATE', 'ENABLE', 'DISABLE', 'RESEND'];
    eventLastKey: unknown = null;

    constructor(
        private _sharedService: SharedService,
        private authService: AuthService,
        private router: Router,
        private userAdminService: UserAdminService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) {
        this.userDataSource = new MatTableDataSource(this.users);
        this.eventDataSource = new MatTableDataSource(this.events);
        this._sharedService.emitChange(this.pageTitle);
    }

    ngOnInit(): void {
        this.authService.getCurrentUser((err, user: User) => {
            this.signedInUser = user;
            if (!this.signedInUser || !this.signedInUser.signedIn) {
                this.router.navigate(['/extra-layout/signin']);
                return;
            }
            this.resolveAccessAndLoad();
        });
    }

    private async resolveAccessAndLoad(): Promise<void> {
        let access = sessionStorage.getItem('access');
        try {
            const attrs = await fetchUserAttributes();
            const live = attrs?.['custom:access'];
            if (live) {
                access = live;
                sessionStorage.setItem('access', live);
            }
        } catch {
            // Fall back to whatever sessionStorage holds.
        }
        if (access === 'SU') {
            this.illegalUser = false;
            this.loadUsers();
            this.loadEvents();
        } else {
            this.illegalUser = true;
        }
    }

    // --- User List ---

    loadUsers(): void {
        this.usersLoading = true;
        this.usersError = '';
        this.userAdminService.getUsers().subscribe({
            next: (response) => {
                this.users = response.users;
                this.userDataSource = new MatTableDataSource(this.users);
                setTimeout(() => this.userDataSource.paginator = this.userPaginator);
                setTimeout(() => this.userDataSource.sort = this.userSort);
                this.usersLoading = false;
            },
            error: (err) => {
                this.usersLoading = false;
                this.usersError = this.getErrorMessage(err);
            }
        });
    }

    applyUserFilter(filterValue: string): void {
        this.userDataSource.filter = filterValue.trim().toLowerCase();
    }

    // --- User Actions ---

    openAddUserDialog(): void {
        this.openUserDialog({ mode: 'create' });
    }

    openEditUserDialog(user: CognitoUser): void {
        this.openUserDialog({ mode: 'edit', user });
    }

    confirmEnableUser(user: CognitoUser): void {
        this.openConfirmDialog({
            title: 'Enable User',
            message: `Are you sure you want to enable user "${user.username}"?`,
            confirmText: 'Enable'
        }, () => this.runUserAction(
            this.userAdminService.enableUser(user.username),
            `User "${user.username}" enabled.`,
            true
        ));
    }

    confirmDisableUser(user: CognitoUser): void {
        this.openConfirmDialog({
            title: 'Disable User',
            message: `Are you sure you want to disable user "${user.username}"?`,
            confirmText: 'Disable'
        }, () => this.runUserAction(
            this.userAdminService.disableUser(user.username),
            `User "${user.username}" disabled.`,
            true
        ));
    }

    confirmResendPassword(user: CognitoUser): void {
        this.openConfirmDialog({
            title: 'Resend Temporary Password',
            message: `Resend a temporary password to user "${user.username}"?`,
            confirmText: 'Resend'
        }, () => this.runUserAction(
            this.userAdminService.resendPassword(user.username),
            `Temporary password resent to "${user.username}".`,
            false
        ));
    }

    // --- User Events ---

    loadEvents(append: boolean = false): void {
        this.eventsLoading = true;
        this.eventsError = '';
        this.userAdminService.getUserEvents({
            username: this.eventUsername || undefined,
            start_date: this.eventStartDate || undefined,
            end_date: this.eventEndDate || undefined,
            action: this.eventAction || undefined,
            last_evaluated_key: append ? this.eventLastKey : undefined
        }).subscribe({
            next: (response) => {
                const items = response.Items || [];
                this.events = append ? [...this.events, ...items] : items;
                this.eventLastKey = response.LastEvaluatedKey || null;
                this.eventDataSource = new MatTableDataSource(this.events);
                setTimeout(() => this.eventDataSource.paginator = this.eventPaginator);
                setTimeout(() => this.eventDataSource.sort = this.eventSort);
                this.eventsLoading = false;
            },
            error: (err) => {
                this.eventsLoading = false;
                this.eventsError = this.getErrorMessage(err);
            }
        });
    }

    loadMoreEvents(): void {
        if (this.eventLastKey) {
            this.loadEvents(true);
        }
    }

    downloadReport(): void {
        const target = document.getElementById('userEventsReport');
        if (!target) {
            return;
        }
        html2canvas(target).then((canvas) => {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = 208;
            const margin = 10;
            const dateStr = new Date().toISOString().split('T')[0];
            const domain = window.location.hostname;

            pdf.setFontSize(14);
            pdf.text('User Events Report', margin, margin + 5);
            pdf.setFontSize(10);
            pdf.text(`Date: ${dateStr}`, margin, margin + 12);
            pdf.text(`Domain: ${domain}`, margin, margin + 18);

            const imgWidth = pageWidth - (2 * margin);
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin + 25, imgWidth, imgHeight);
            pdf.save(`user-events-${dateStr}.pdf`);
        });
    }

    filterEvents(
        dateRangeStart: HTMLInputElement,
        dateRangeEnd: HTMLInputElement,
        usernameInput: HTMLInputElement,
        actionValue: string
    ): void {
        this.eventStartDate = dateRangeStart.value;
        this.eventEndDate = dateRangeEnd.value;
        this.eventUsername = usernameInput.value.trim();
        this.eventAction = actionValue || '';
        this.eventLastKey = null;
        this.loadEvents(false);
    }

    // --- Helpers ---

    private openUserDialog(data: UserDialogData): void {
        const dialogRef = this.dialog.open(UserAdminDialogComponent, {
            width: '500px',
            data
        });
        dialogRef.afterClosed().subscribe(saved => {
            if (saved) {
                this.loadUsers();
            }
        });
    }

    private openConfirmDialog(data: ConfirmDialogData, onConfirm: () => void): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            data
        });
        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                onConfirm();
            }
        });
    }

    private runUserAction(request$: Observable<unknown>, successMessage: string, reload: boolean): void {
        this.usersLoading = true;
        request$.subscribe({
            next: () => {
                this.usersLoading = false;
                this.snackBar.open(successMessage, 'Dismiss', { duration: 4000 });
                if (reload) {
                    this.loadUsers();
                }
            },
            error: (err) => {
                this.usersLoading = false;
                this.snackBar.open(this.getErrorMessage(err), 'Dismiss', { duration: 6000, panelClass: 'snack-error' });
            }
        });
    }

    private getErrorMessage(err: { status?: number }): string {
        switch (err && err.status) {
            case 400: return 'Bad Request. Please verify the input values.';
            case 401: return 'Unauthorized. Please login again.';
            case 403: return 'Access denied. You do not have permission to perform this action.';
            case 404: return 'Resource not found.';
            case 409: return 'Conflict. The user may already exist.';
            case 500: return 'Server error. Please try again later.';
            case 502: return 'Bad Gateway. The server is temporarily unavailable.';
            case 503: return 'Service Unavailable. Please try again later.';
            case 504: return 'Gateway Timeout. Server took too long to respond.';
            default:  return 'Unexpected error occurred. Please try again.';
        }
    }
}
